import { useState } from "react";
import {
  CRITERIA, GROUPS, LIST_UNKNOWN, MEASURES, PRESETS, RANKINGS, criteriaIn, isPreviewable, isSet, knownChoices,
  parseList, optionLabel, presetIsOn, presetSays, rankingOf, setCount, withBound, withChoice, withPreset, withTypedList,
  type Criterion, type Filters, type FilterValue, type Measure, type RankKey, type RankState,
} from "../lib/campaignFilters";
import { StageWordsControl } from "./StageWords";
import { ListUnknownBox, UnknownBox } from "./UnknownBox";

/** The measure a range key sets, for its box. */
const measureOfKey = (key: string): Measure | undefined =>
  (Object.keys(MEASURES) as Measure[]).find((m) => (MEASURES[m].keys as readonly string[]).includes(key));

/* Every criterion a campaign can carry, with the six common ones first.
 *
 * James, 2026-09-15: the jet cockpit. A person building a campaign knows what
 * they are doing and wants every setting the engine has, with the common ones
 * first and the rest folded away rather than removed. The engine knows
 * thirty-one and this screen offered six until now, so a campaign could not
 * say most of what a campaign is for.
 *
 * Three rules it works to.
 *
 * Nothing set is ever hidden. A group that holds a value says how many in its
 * own header, whether it is open or shut, for the same reason the book screens
 * keep a chip per filter: a narrowed list that looks unnarrowed is the screen
 * lying about its own contents.
 *
 * A boolean is three states, not two. Absent means the campaign has no
 * opinion, and it is a different thing from false. `has_email: false` is a
 * real campaign, the one that finds the records worth enriching, so a checkbox
 * would collapse two of the three and quietly drop the useful one.
 *
 * The preview says what it cannot count. Nine of the thirty-one mean the same
 * thing as a column on api.property. The other twenty-two need joins the api
 * schema does not expose, so they are marked rather than silently ignored, and
 * the count on the right is a ceiling whenever one is set.
 */
export function CriteriaEditor({
  token, filters, onChange, ranking, onRanking, groupsOnly = false, omit = [], unknownCounts,
}: {
  /** For the one control that reads a list from the engine, the stage words. */
  token: string;
  filters: Filters;
  onChange: (next: Filters) => void;
  ranking: RankState;
  onRanking: (next: RankKey) => void;
  /** Only the folded groups, every criterion in its group, for the builder
   *  whose four questions ask the segment, the place and the size above. */
  groupsOnly?: boolean;
  /** Criteria asked elsewhere on the screen, left out of the groups. */
  omit?: readonly string[];
  /** How many matching properties have no value, per range, for its box. */
  unknownCounts?: Partial<Record<string, number | null>> | undefined;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [presetNote, setPresetNote] = useState<string | null>(null);

  const set = (key: string, value: FilterValue | undefined) => {
    const next = { ...filters };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  const applyPreset = (label: string) => {
    const p = PRESETS.find((x) => x.label === label);
    if (!p) return;
    const turningOn = !presetIsOn(filters, p);
    const next = withPreset(filters, p);
    onChange(next);
    // A segment sets how the run ranks as well as what it matches. Turning a
    // segment off leaves the ranking as it is, because that is its own choice.
    if (turningOn) onRanking(p.rankBy);
    setPresetNote(p.note ?? null);
    // Open the groups the preset just wrote into, so it is visibly filters
    // rather than something the screen did behind the reader.
    // Only groups that actually contain something the segment changed. A core
    // criterion is shown above the groups and filtered out of them, so opening
    // its group showed the reader a panel where nothing had moved: choosing
    // New Construction sets standing_stock, which belongs to Building, and
    // Building opened on three untouched controls. Same mistake as the "1 set"
    // chip counting criteria that live above the expander.
    const touched: Record<string, boolean> = { ...open };
    for (const key of Object.keys(next)) {
      const c = CRITERIA.find((x) => x.key === key);
      if (c && !c.core) touched[c.group] = true;
    }
    setOpen(touched);
  };

  const core = CRITERIA.filter((c) => c.core);
  const inGroup = (g: (typeof GROUPS)[number]) => criteriaIn(g)
    .filter((c) => !omit.includes(c.key) && (groupsOnly || !c.core));

  if (groupsOnly) {
    return (
      <div className="criteria">
        {GROUPS.map((g) => {
          const rest = inGroup(g);
          if (!rest.length) return null;
          const n = rest.filter((c) => isSet(filters[c.key])).length;
          const isOpen = open[g] ?? false;
          return (
            <div className="criteria-group" key={g}>
              <button type="button" className="group-head" aria-expanded={isOpen}
                      onClick={() => setOpen({ ...open, [g]: !isOpen })}>
                {g}
                {n ? <span className="chip">{n} set</span> : null}
                <span className="muted" style={{ marginLeft: ".4rem" }}>{isOpen ? "hide" : "show"}</span>
              </button>
              {isOpen ? (
                <div className="fields">
                  {rest.map((c) => <Control key={c.key} c={c} filters={filters} set={set} token={token} onChange={onChange} unknownCounts={unknownCounts} />)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="criteria">
      <div className="bar">
        {/* Nestor's own campaign builder opens with "Which target segment is
            this campaign going after?" and offers these four. Calling the row
            "Start from" hid the first question a campaign answers behind a
            label about buttons. Transcript: 2026-08-27 In-Office Session
            Part 2. */}
        <span className="field-label">Target segment</span>
        {PRESETS.map((p) => (
          <button type="button" key={p.label}
                  aria-pressed={presetIsOn(filters, p)}
                  className={presetIsOn(filters, p) ? "pressed" : undefined}
                  onClick={() => applyPreset(p.label)}>
            {p.label}
          </button>
        ))}
      </div>
      {/* What the pressed segment set, so a segment is visibly a set of
          filters and a ranking rather than something done out of sight. */}
      {PRESETS.filter((p) => presetIsOn(filters, p)).map((p) => (
        <p className="hint" key={p.label}>{presetSays(p)}</p>
      ))}
      {presetNote ? <p className="muted" style={{ maxWidth: "44rem" }}>{presetNote}</p> : null}

      <div className="bar">
        <span className="field-label">Rank by</span>
        {RANKINGS.map((r) => (
          <button type="button" key={r.key}
                  aria-pressed={ranking === r.key}
                  className={ranking === r.key ? "pressed" : undefined}
                  onClick={() => onRanking(r.key)}>
            {r.label}
          </button>
        ))}
      </div>
      <p className="hint">
        {ranking === "engine"
          ? "Set in the engine. Saving keeps it."
          : rankingOf(ranking).says}
      </p>

      <div className="fields">
        {core.map((c) => <Control key={c.key} c={c} filters={filters} set={set} token={token} onChange={onChange} unknownCounts={unknownCounts} />)}
      </div>

      {GROUPS.map((g) => {
        const rest = criteriaIn(g).filter((c) => !c.core);
        if (!rest.length) return null;
        const n = setCount(filters, g);
        const isOpen = open[g] ?? false;
        return (
          <div className="criteria-group" key={g}>
            <button type="button" className="group-head"
                    aria-expanded={isOpen}
                    onClick={() => setOpen({ ...open, [g]: !isOpen })}>
              {g}
              {n ? <span className="chip">{n} set</span> : null}
              <span className="muted" style={{ marginLeft: ".4rem" }}>
                {isOpen ? "hide" : "show"}
              </span>
            </button>
            {isOpen ? (
              <div className="fields">
                {rest.map((c) => <Control key={c.key} c={c} filters={filters} set={set} token={token} onChange={onChange} unknownCounts={unknownCounts} />)}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Control({
  c, filters, set, token, onChange, unknownCounts,
}: {
  c: Criterion;
  filters: Filters;
  set: (key: string, value: FilterValue | undefined) => void;
  token: string;
  onChange: (next: Filters) => void;
  unknownCounts?: Partial<Record<string, number | null>> | undefined;
}) {
  const v = filters[c.key];
  const counted = isPreviewable(c.key);
  // Year of and the unknowns list are set by the builder's sections and the
  // box under each range, never as a control of their own.
  if (c.type === "year_of" || c.type === "unknowns") return null;
  // A range's bound, with the box that keeps its properties with no value,
  // outside the label so one label does not hold another.
  const measure = c.type === "number" ? measureOfKey(c.key) : undefined;
  if (measure) {
    return (
      <div>
        <label>
          <div className="field-label">{c.label}</div>
          <input type="number" min={0} style={{ width: "100%" }}
                 value={v === undefined ? "" : String(v)}
                 onChange={(e) => onChange(withBound(filters, c.key,
                   e.target.value === "" ? undefined : Number(e.target.value)))} />
          {c.help ? <div className="muted" style={{ fontSize: 12 }}>{c.help}</div> : null}
        </label>
        <UnknownBox filters={filters} measure={measure} count={unknownCounts?.[measure]} onChange={onChange} />
      </div>
    );
  }

  const control = (
    <label>
      <div className="field-label">
        {c.label}
        {!counted && isSet(v) ? (
          <span className="chip" title="Not included in the count.">
            not counted
          </span>
        ) : null}
      </div>

      {c.type === "text_list" ? (
        <>
          {/* Read when the field is left, as the builder's City is. Read on
              every keystroke, a typed comma was parsed away before the next
              value could follow it, so "A, B" became "AB". The key remounts
              the field when the stored list changes from elsewhere, such as a
              segment. Property class keeps "unknown" in its stored list for
              the box below, so the text shows the known classes only. */}
          <input
            key={knownChoices(c.key, (v as string[]) ?? []).join(", ")}
            defaultValue={knownChoices(c.key, (v as string[]) ?? []).join(", ")}
            onBlur={(e) => LIST_UNKNOWN[c.key]
              ? onChange(withTypedList(filters, c.key, parseList(e.currentTarget.value)))
              : set(c.key, parseList(e.currentTarget.value))}
            style={{ width: "100%" }}
          />
          {/* A format, which the standard allows a help line for. It used to
              sit in the placeholder as "CA, CO", where a format reads as a
              value the box already holds.

              Only when the criterion says nothing more specific. State carried
              this line and "Two-letter codes, comma separated." stacked on top
              of each other, which is two lines saying one thing. */}
          {c.help ? null : (
            <p className="muted" style={{ margin: ".15rem 0 0" }}>Comma separated.</p>
          )}
        </>
      ) : c.type === "number" ? (
        <input type="number" min={0} style={{ width: "100%" }}
               value={v === undefined ? "" : String(v)}
               onChange={(e) => set(c.key, e.target.value === "" ? undefined : Number(e.target.value))} />
      ) : c.type === "stage_words" ? (
        <StageWordsControl token={token} value={(v as string[]) ?? []}
                           onChange={(statuses) => set(c.key, statuses)} />
      ) : c.type === "boolean" ? (
        /* Three states. Absent is the campaign having no opinion, and it is a
           different thing from no. */
        <select value={v === true ? "yes" : v === false ? "no" : ""}
                onChange={(e) => set(c.key, e.target.value === "" ? undefined
                                          : e.target.value === "yes")}>
          {/* "Any" rather than "no opinion". The state is the campaign not
              filtering on this at all, and Any says that in the word a person
              already uses for it. The three states are unchanged. */}
          <option value="">Any</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      ) : (
        /* enum_list and chargers are the same control. Chargers has three
           fixed values and unknown is one of them, because nobody having
           looked is an answer. A list whose value can be not known offers
           its known values here and the not known one as the box below,
           with its count (LIST_UNKNOWN, James, 2026-10-22). */
        <div className="checks">
          {knownChoices(c.key, c.type === "chargers" ? ["present", "absent", "unknown"] : (c.options ?? []))
            .map((o) => {
              const cur = (v as string[]) ?? [];
              const on = cur.includes(o);
              return (
                <label key={o} className="check">
                  <input type="checkbox" checked={on} onChange={() =>
                    LIST_UNKNOWN[c.key] ? onChange(withChoice(filters, c.key, o, !on))
                      : set(c.key, on ? cur.filter((x) => x !== o) : [...cur, o])} />
                  {" "}{optionLabel(o)}
                </label>
              );
            })}
        </div>
      )}

      {c.help ? <div className="muted" style={{ fontSize: 12 }}>{c.help}</div> : null}
    </label>
  );
  if (!LIST_UNKNOWN[c.key]) return control;
  // The box sits outside the label, so one label does not hold another.
  return (
    <div>
      {control}
      <ListUnknownBox filters={filters} listKey={c.key} count={unknownCounts?.[c.key]} onChange={onChange} />
    </div>
  );
}
